import React from "react";
import { WidgetWrapper } from "../../../../components/WidgetWrapper/WidgetWrapper";
import { EditField } from "../../../../components/EditPanel/types";
import {
  TypingIndicatorWidgetProps,
  TypingIndicatorWidgetState,
} from "./types";
import { useWidgetEditable } from "../../../../hooks/useWidgetEditable";
import { themeColor } from "../../../../types/color";

const defaultState: TypingIndicatorWidgetState = {
  // Container
  containerBgColor: themeColor("background"),
  containerPaddingX: 16,
  containerPaddingY: 12,
  containerBorderRadius: 18,
  containerBorderRadiusBottomLeft: 5,

  // Dots
  dotColor: themeColor("textPrimary"),
  dotSize: 8,
  dotSpacing: 2,

  // Animation
  animationDuration: 1.4,

  // Status text
  statusTextColor: themeColor("textPrimary"),
  statusFontSize: 12,
  statusMarginTop: 8,
};

const editFields: EditField[] = [
  // Container
  { key: "containerBgColor", label: "Container Background", type: "color" },
  {
    key: "containerPaddingX",
    label: "Container Padding X",
    type: "number",
    min: 8,
    max: 24,
    step: 2,
  },
  {
    key: "containerPaddingY",
    label: "Container Padding Y",
    type: "number",
    min: 6,
    max: 20,
    step: 2,
  },
  {
    key: "containerBorderRadius",
    label: "Container Border Radius",
    type: "number",
    min: 0,
    max: 30,
    step: 2,
  },
  {
    key: "containerBorderRadiusBottomLeft",
    label: "Bottom Left Radius",
    type: "number",
    min: 0,
    max: 20,
    step: 1,
  },

  // Dots
  { key: "dotColor", label: "Dot Color", type: "color" },
  {
    key: "dotSize",
    label: "Dot Size",
    type: "number",
    min: 4,
    max: 16,
    step: 1,
  },
  {
    key: "dotSpacing",
    label: "Dot Spacing",
    type: "number",
    min: 1,
    max: 8,
    step: 1,
  },

  // Animation
  {
    key: "animationDuration",
    label: "Animation Duration (s)",
    type: "number",
    min: 0.5,
    max: 3,
    step: 0.1,
  },

  // Status text
  { key: "statusTextColor", label: "Status Text Color", type: "color" },
  {
    key: "statusFontSize",
    label: "Status Font Size",
    type: "number",
    min: 10,
    max: 16,
    step: 1,
  },
  {
    key: "statusMarginTop",
    label: "Status Margin Top",
    type: "number",
    min: 4,
    max: 16,
    step: 2,
  },
];

export const TypingIndicatorWidget: React.FC<TypingIndicatorWidgetProps> = ({
  initialState = {},
  widgetId,
  displayName,
  children,
}) => {
  const {
    state,
    isLoading,
    isSaving,
    handleEditChange,
    handleSave,
    themeColors,
    resolveColor,
    canSave,
  } = useWidgetEditable<TypingIndicatorWidgetState>(
    widgetId,
    { ...defaultState, ...initialState },
    editFields,
  );

  // Show loading state while fetching theme config
  if (isLoading) {
    return <></>;
  }

  // Render the children with current state
  const widgetContent = children(state, resolveColor);

  // Wrap with WidgetWrapper for edit capability
  return (
    <WidgetWrapper
      widgetId={widgetId}
      displayName={displayName}
      editFields={editFields}
      editValues={state}
      onEditChange={handleEditChange}
      onSave={canSave ? handleSave : undefined}
      isSaving={isSaving}
      themeColors={themeColors}
    >
      {widgetContent}
    </WidgetWrapper>
  );
};
